<template>
  <div>
    <CRow>
      <CCol sm="12">
        <CCard>
          <CCardHeader>
            <b>Dokumentasi - Notifikasi iCare (Planted / Off-Planted / Absensi)</b>
          </CCardHeader>
          <CCardBody>
            <p class="text-muted">
              Halaman ini menjelaskan aturan otomatis yang digunakan sistem untuk mengingatkan ICL, Core Team,
              dan Member terkait kehadiran di iCare. Penjelasan dibuat sederhana dan tidak menggunakan istilah teknis.
            </p>

            <hr>

            <h5>📋 Summary Table (English)</h5>
            <div class="table-responsive">
              <table class="table table-sm table-bordered">
                <thead>
                  <tr>
                    <th>Case</th>
                    <th>Description (Meaning)</th>
                    <th>Title</th>
                    <th>Body</th>
                    <th>Send To</th>
                    <th>Notification Type</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>1. Member Planted</td>
                    <td>A member/visitor has attended iCare 3 times in a row and is now recommended to be marked as Planted.</td>
                    <td>🎉 Member Planted</td>
                    <td>"[Name] has attended iCare 3 times in a row. Let's celebrate and keep walking with them!"</td>
                    <td>ICL &amp; Core Team</td>
                    <td>ICARE_PLANTED (14)</td>
                  </tr>
                  <tr>
                    <td>2. Member Off-Planted</td>
                    <td>A member who was already Planted has not attended iCare for 3 consecutive months.</td>
                    <td>⚠️ Member Off-Planted</td>
                    <td>"[Name] has not attended iCare for 3 consecutive months. Please reach out and check in with them."</td>
                    <td>ICL &amp; Core Team</td>
                    <td>ICARE_OFF_PLANTED (15)</td>
                  </tr>
                  <tr>
                    <td>3. Absent 1 Month (to Member)</td>
                    <td>A gentle, personal check-in after someone has been absent from iCare for 1 month.</td>
                    <td>We miss you! 💛</td>
                    <td>"How are you? We haven't seen you in iCare for a month. We hope you're doing well!"</td>
                    <td>The person themselves</td>
                    <td>ICARE_MEMBER_ABSENT_1MONTH (16)</td>
                  </tr>
                  <tr>
                    <td>4. Absent 2 Months (to Member)</td>
                    <td>A more heartfelt personal check-in after someone has been absent from iCare for 2 consecutive months.</td>
                    <td>We miss you! 💛</td>
                    <td>"How are you? We missed you… we haven't seen you in iCare for 2 months. We'd love to see you again!"</td>
                    <td>The person themselves</td>
                    <td>ICARE_MEMBER_ABSENT_2MONTH (17)</td>
                  </tr>
                  <tr>
                    <td>5. Absent 2 Months (to Leader)</td>
                    <td>Same 2-month absence event as case 4, escalated so leadership can follow up.</td>
                    <td>📋 Member Needs Follow-up</td>
                    <td>"[Name] has not attended iCare for 2 consecutive months. Please reach out and check in with them."</td>
                    <td>ICL &amp; Core Team</td>
                    <td>ICARE_ABSENCE_LEADER_NOTICE (18)</td>
                  </tr>
                  <tr>
                    <td>6. Absent 3 Months, Not Yet Planted</td>
                    <td>A person who is not yet Planted has not attended iCare for 3 consecutive months — the most serious stage for someone who was never planted. (Different from case 2: this is for people who were never Planted; someone already Planted follows case 2 instead.)</td>
                    <td>🚨 Member Needs Urgent Follow-up</td>
                    <td>"[Name] has not attended iCare for 3 consecutive months and is not yet planted. Please reach out and check in with them."</td>
                    <td>ICL &amp; Core Team</td>
                    <td>ICARE_MEMBER_ABSENT_3MONTH_LEADER_NOTICE (19)</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <hr>

            <h5>🎉 1. Member Planted</h5>
            <p><b>Arti:</b> Member atau visitor dianggap sudah tertanam (rutin hadir) di iCare.</p>
            <p><b>Kapan notifikasi dikirim:</b> <u>Langsung/real-time</u>, begitu ICL atau Core Team menyimpan absensi iCare, sistem langsung memeriksa apakah ada orang yang baru saja hadir di iCare sebanyak <b>3 kali berturut-turut</b> tanpa terputus. Tidak perlu menunggu jadwal mingguan.</p>
            <p><b>Penerima notifikasi:</b> ICL (Leader) dan Core Team dari group iCare yang bersangkutan.</p>
            <p><b>Isi pesan:</b> "[Name] has attended iCare 3 times in a row. Let's celebrate and keep walking with them!"</p>
            <p><b>Catatan penting:</b> Notifikasi ini hanya berupa <u>pengingat/rekomendasi</u>. Status "Planted" tidak berubah secara otomatis. ICL atau admin tetap harus mengubah status tersebut secara manual melalui menu iCare apabila memang dinilai sudah layak. Selama status belum diubah dan orang tersebut tetap hadir, pengingat yang sama tidak akan dikirim berulang dalam minggu yang sama — paling cepat muncul lagi di minggu berikutnya.</p>

            <hr>

            <h5>⚠️ 2. Member Off-Planted</h5>
            <p><b>Arti:</b> Member yang sebelumnya sudah berstatus "Planted", namun mulai jarang atau tidak hadir kembali.</p>
            <p><b>Kapan notifikasi dikirim:</b> Ketika member yang sudah berstatus "Planted" ternyata <b>tidak hadir selama 3 bulan berturut-turut</b>.</p>
            <p><b>Penerima notifikasi:</b> ICL (Leader) dan Core Team dari group iCare yang bersangkutan.</p>
            <p><b>Isi pesan:</b> "[Name] has not attended iCare for 3 consecutive months. Please reach out and check in with them."</p>
            <p><b>Tujuan:</b> Agar ICL dan Core Team dapat segera menindaklanjuti dan mengecek kondisi anggota tersebut sebelum kehilangan kontak lebih lama.</p>
            <p><b>Catatan penting:</b> Selama status "Planted" orang tersebut belum diubah menjadi "Off-Planted" secara manual oleh ICL/admin, notifikasi ini akan <u>terus terkirim ulang setiap minggu</u> (berbeda dari notifikasi absen di poin 3-6 yang hanya terkirim sekali per periode absen).</p>

            <hr>

            <h5>💛 3. Pengingat kepada Member: Absen 1 Bulan</h5>
            <p><b>Arti:</b> Berbeda dari poin 1 dan 2 di atas, notifikasi ini dikirim langsung kepada orang yang bersangkutan, bukan kepada ICL.</p>
            <p><b>Kapan notifikasi dikirim:</b> Ketika seseorang (member, visitor, Core Team, maupun ICL) sudah <b>1 bulan tidak hadir</b> di iCare-nya.</p>
            <p><b>Penerima notifikasi:</b> Orang yang bersangkutan saja.</p>
            <p><b>Isi pesan:</b> "How are you? We haven't seen you in iCare for a month. We hope you're doing well!"</p>
            <p><b>Tujuan:</b> Sebagai sapaan ringan dan personal, sebelum memasuki tahap "2 bulan" yang lebih serius.</p>

            <hr>

            <h5>💛📋 4. Absen 2 Bulan Berturut-turut (Notifikasi ke Semua Pihak)</h5>
            <p><b>Arti:</b> Orang tersebut sudah <b>2 bulan berturut-turut tidak hadir sama sekali</b>. Ini merupakan tahap yang lebih serius, sehingga sistem mengirimkan notifikasi kepada 3 pihak sekaligus dari satu kejadian yang sama:</p>
            <ol>
              <li><b>Kepada orang yang bersangkutan</b> — pesan personal: "How are you? We missed you… we haven't seen you in iCare for 2 months. We'd love to see you again!"</li>
              <li><b>Kepada ICL (Leader)</b> — pengingat untuk menindaklanjuti: "[Name] has not attended iCare for 2 consecutive months. Please reach out and check in with them."</li>
              <li><b>Kepada Core Team</b> — agar seluruh pihak terkait mengetahui dan dapat membantu menindaklanjuti bersama.</li>
            </ol>
            <p><b>Tujuan:</b> Bukan sekadar memberi "label" bahwa seseorang sudah lama tidak hadir, tetapi benar-benar mendorong adanya tindak lanjut dan menjaga hubungan dengan orang tersebut.</p>

            <hr>

            <h5>🚨 5. Absen 3 Bulan Berturut-turut (Khusus Belum Planted)</h5>
            <p><b>Arti:</b> Orang yang <u>belum berstatus Planted</u> sudah <b>3 bulan berturut-turut tidak hadir</b> sama sekali. Ini tahap paling serius untuk orang yang belum tertanam.</p>
            <p><b>Kapan notifikasi dikirim:</b> Ketika orang yang belum planted absen selama 3 bulan berturut-turut.</p>
            <p><b>Penerima notifikasi:</b> ICL (Leader) dan Core Team saja (tidak dikirim ke orang yang bersangkutan, karena sudah pernah ditegur di tahap 1 dan 2 bulan).</p>
            <p><b>Isi pesan:</b> "[Name] has not attended iCare for 3 consecutive months and is not yet planted. Please reach out and check in with them."</p>
            <p><b>Catatan:</b> Khusus untuk member yang <u>sudah</u> berstatus Planted lalu absen 3 bulan, notifikasinya bukan ini, melainkan notifikasi <b>"Member Off-Planted"</b> (poin 2 di atas) — supaya tidak ada dua notifikasi berbeda untuk kejadian yang sama.</p>

            <hr>

            <h5>📌 Hal-hal Penting yang Perlu Diketahui</h5>
            <ul>
              <li>Notifikasi <b>Member Planted</b> (poin 1) dikirim <b>real-time</b>, langsung setelah absensi iCare disimpan.</li>
              <li>Notifikasi <b>Off-Planted, Absen 1/2/3 Bulan</b> (poin 2-5) berjalan <b>otomatis setiap minggu</b> (setiap hari Senin sore) — sistem melakukan pengecekan sendiri, tidak perlu dijalankan secara manual.</li>
              <li>Apabila seseorang <b>belum memiliki akun aplikasi atau belum pernah login di perangkatnya</b>, notifikasi akan otomatis dilewati (tidak menimbulkan error, hanya saja tidak ada perangkat yang dapat dikirimi).</li>
              <li>
                Aturan pengulangan notifikasi <b>berbeda untuk 2 kelompok kasus</b> di atas:
                <ul>
                  <li><b>Member Planted (poin 1)</b> dan <b>Member Off-Planted (poin 2)</b> — akan <b>terus terkirim ulang setiap minggu</b> selama status yang direkomendasikan belum diubah secara manual oleh ICL/admin. Ini disengaja, supaya ICL tetap diingatkan sampai benar-benar ditindaklanjuti.</li>
                  <li><b>Absen 1/2/3 Bulan (poin 3-6)</b> — hanya terkirim <b>sekali</b> untuk satu periode absen yang sama, tidak berulang tiap minggu, kecuali orangnya sempat hadir lagi lalu absen lagi (baru dianggap periode absen baru).</li>
                </ul>
              </li>
              <li>Status "Planted" atau "Off-Planted" yang sesungguhnya (yang terlihat pada data member) <b>harus diubah secara manual</b> oleh ICL atau admin melalui menu iCare. Notifikasi di atas hanya bersifat pengingat, bukan yang mengubah status tersebut secara langsung.</li>
              <li>Notifikasi <b>tidak akan pernah dikirim ke diri sendiri</b>. Contoh: jika yang perlu ditindaklanjuti (di-set Planted, atau absen berkepanjangan) adalah ICL atau Core Team itu sendiri, ia tidak akan menerima notifikasi tentang dirinya sendiri — namun ICL/Core Team lain di group yang sama tetap menerima notifikasinya seperti biasa.</li>
            </ul>

            <hr>

            <h5>🔍 Cara Memeriksa Status Pengiriman Notifikasi</h5>
            <p>
              Buka menu <b>Settings &gt; Notification Logs</b> untuk melihat seluruh notifikasi yang pernah
              dikirim oleh sistem, kepada siapa, jenis notifikasinya apa, berhasil atau gagal, beserta alasan
              apabila gagal (misalnya perangkat tidak terdaftar, atau notifikasi diblokir oleh pengaturan HP).
            </p>
            <p>
              Untuk melihat perangkat (device) apa saja yang terdaftar atas nama seseorang, buka detail orang
              tersebut pada menu iCare/People, kemudian pilih tab <b>Devices</b>.
            </p>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
export default {
  name: 'Documentation',
}
</script>
